// Used in side-panel-right.component.html

// Angular
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { Router } from "@angular/router";

// RXJS/Store
import * as fromCreateStore from "#components/content-library/assign-cart/content-builder-store";
import * as assetsActions from "#components/content-library/assign-cart/content-builder-store/actions/assets.actions";
import * as RouterStore from "#core/router-store";
import * as fromCoursesStore from "#core/teacher-classes/store";
import * as fromAssignStore from "#teacher/assign/store";
import { Actions, ofType } from "@ngrx/effects";
import { Store, select } from "@ngrx/store";
import { Observable, Subject, Subscription } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import * as fromCartStore from "../../assign-cart-store";

// Services/Types/Helpers
import { AssignmentHelper } from "#helpers/assignment.helper";
import { LanguageHelper } from "#helpers/language.helper";
import { PacingHelper } from "#helpers/pacing.helper";
import { LocalStorage } from "#services/local-storage.service";
import { SnackBarService } from "#services/snackbar.service";
import { AssetId, EditionId, SubjectId } from "@carnegie-learning/common-types";
import { Asset, Bundle, CustomActivity, Edition, assetActivityTypeIds, assetTypeIds } from "@carnegie-learning/cure-types";
import { AssetsService } from "@carnegie-learning/data-access-cure";
import { ASSETS_SERVICE } from "@carnegie-learning/data-access-angular-cure";
import { LessonsService } from "@carnegie-learning/data-access-angular-passport";
import { FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";
import { AssetTypeHelper, Course, SelectedAssets } from "cli-types";

@Component({
    selector: "assign-cart-side-panel",
    templateUrl: "./assign-cart-side-panel.component.html",
    styleUrls: ["./assign-cart-side-panel.component.scss"],
    standalone: false
})
export class AssignCartSidePanelComponent implements OnInit, OnDestroy {
    totalPacingWarning = false;
    creatingLesson = false;
    editingLesson = false;
    assetInUse$?: Observable<boolean>;
    creatingActivity = false;
    editingActivity = false;
    containsComposite = false;
    cartItems: SelectedAssets = { editionId: "", assets: [] };
    subjectId: SubjectId = "";
    editionId: EditionId = "";
    subs = new Subscription();
    params?: FeathersServicePaginationParameters<Asset>;
    lessonFormData?: Bundle;
    lessonFormValidity = false;
    activityFormData?: CustomActivity;
    activityFormValidity = false;
    onContentLibraryPage = false;
    activeEditions: Edition[] = [];
    preventDoubleClick = false;
    currentPageURL = "";
    assetsPageTab?: number;
    selectedCourse: Course | null;
    updatingBundle = false;
    updatedBundleAssetId: AssetId;
    onResourcePage = false;
    onAssignablesPage = true;
    createAddBundle = false;
    algoliaFailed: boolean;

    @Input() assignCartOpen = false;
    @Output() sidePanelRightClose = new EventEmitter<void>();
    @Output() openLessonBuilder = new EventEmitter<boolean>();
    @Output() setContentBuilderType = new EventEmitter<string>();
    @ViewChild("assetScroller") private assetScroller: ElementRef;

    private ngUnsubscribe = new Subject();

    constructor(
        private assignmentHelper: AssignmentHelper,
        private cartStore: Store<fromCartStore.CartState>,
        private coursesStore: Store<fromCoursesStore.CoursesState>,
        private createStore: Store<fromCreateStore.CreateState>,
        private cdr: ChangeDetectorRef,
        private assetTypeHelper: AssetTypeHelper,
        private router: Router,
        private routerStore: Store<RouterStore.RouterStateUrl>,
        private languageHelper: LanguageHelper,
        private localStorage: LocalStorage,
        private lessonsService: LessonsService,
        private snackBarService: SnackBarService,
        @Inject(ASSETS_SERVICE) private assetsService: AssetsService,
        public dialog: MatDialog,
        private pacingHelper: PacingHelper,
        private assignStore: Store<fromAssignStore.AssignState>,
        private actions$: Actions
    ) {}

    ngOnInit() {
        this.preventDoubleClick = false;

        // Get tab index and set local variables accordingly
        const currentTab = this.localStorage.get("contentLibrarySelectedIndex");
        if (currentTab === 0) {
            this.onResourcePage = true;
            this.onAssignablesPage = false;
        };
        this.createStore
            .pipe(select(fromCreateStore.getSelectedAssets))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((selectedAssets: SelectedAssets) => {
                if (selectedAssets && selectedAssets.assets.length > 0) {
                    // New Bundle created and added to selectedAssets, now go to the Assign page
                    if (
                        this.createAddBundle &&
                        selectedAssets.assets.length === 1
                    ) {
                        this.goToAssignReview(null);
                    }
                    if (
                        this.cartItems &&
                        this.cartItems.assets.length <
                            selectedAssets.assets.length &&
                        this.editingLesson
                    ) {
                        // activities added to cart during edit bundle
                        this.cartStore.dispatch(
                            fromCartStore.setLessonFormValidity({
                                payload: true,
                            })
                        );
                    } else if (
                        this.cartItems.assets.length &&
                        this.cartItems.assets.length <
                            selectedAssets.assets.length &&
                        this.assignCartOpen
                    ) {
                        this.scrollToLastAddedAsset(); // a new item was just added to the assign cart. Scroll down to it.
                    }
                    this.cartItems = selectedAssets;
                    if (
                        this.selectedCourse &&
                        this.selectedCourse.editionId !== this.editionId
                    ) {
                        this.editionId = this.selectedCourse.editionId;
                    }
                    // If no more lessons/assessments in cart, allow Lesson conversion.
                    const lessonFound = selectedAssets.assets.find(
                        (a) => a.assetTypeId === assetTypeIds.BUNDLE
                    );
                    if (!lessonFound) {
                        this.cartStore.dispatch(
                            fromCartStore.setContainsComposite({
                                payload: false,
                            })
                        );
                    }

                    if (!this.editionId) {
                        const cartItem = this.cartItems.assets.find(
                            (obj) => obj.editionIds[0] // TODO: CURE: how do we identify composites like this?
                        );

                        this.cartStore
                            .pipe(select(fromCartStore.getProgramEditionId))
                            .pipe(first(), takeUntil(this.ngUnsubscribe))
                            .subscribe((editionIdFromStore: EditionId) => {
                                if (editionIdFromStore) {
                                    this.editionId = editionIdFromStore;
                                } else if (cartItem) {
                                    this.editionId = cartItem.editionIds[0];
                                }
                            });
                    }
                } else {
                    this.cartItems = { ...this.cartItems, assets: [] };
                    this.cartStore.dispatch(
                        fromCartStore.setContainsComposite({ payload: false })
                    );
                }
                this.cdr.detectChanges();
            });

        this.coursesStore
            .pipe(select(fromCoursesStore.getAllActiveEditions))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((editions: Edition[]) => {
                if (editions && editions.length > 0) {
                    this.activeEditions = editions;
                }
                this.cdr.detectChanges();
            });

        this.cartStore
            .pipe(select(fromCartStore.getCreatingLesson))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((response: boolean) => {
                this.creatingLesson = response;
                this.cdr.detectChanges();
            });
        this.cartStore
            .pipe(select(fromCartStore.getEditingLesson))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((response: boolean) => {
                this.editingLesson = response;
                this.cdr.detectChanges();
            });

        this.cartStore
            .pipe(select(fromCartStore.getCreatingActivity))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((response: boolean) => {
                this.creatingActivity = response;
                this.cdr.detectChanges();
            });

        this.cartStore
            .pipe(select(fromCartStore.getEditingActivity))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((response: boolean) => {
                this.editingActivity = response;
                this.cdr.detectChanges();
            });

        this.routerStore
            .pipe(select(RouterStore.getRouterState))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((router) => {
                if (router) {
                    this.currentPageURL = router.state.url;
                    if (router.state.params.subjectId) {
                        this.subjectId = router.state.params.subjectId;
                    } else if (
                        this.cartItems &&
                        this.cartItems.assets.length > 0
                    ) {
                        const cartItem = this.cartItems.assets.find(
                            (obj) => obj.subjectId
                        );
                        if (cartItem) {
                            this.subjectId = cartItem.subjectId;
                        }
                    }
                    if (router.state.params.editionId) {
                        this.editionId = router.state.params.editionId;
                        if (!this.subjectId && this.activeEditions.length) {
                            const foundEdition = this.activeEditions.find(
                                (edit) => edit.editionId === this.editionId
                            );
                            if (foundEdition) {
                                this.subjectId = foundEdition.subjectId;
                            }
                        }
                    } else if (
                        this.cartItems &&
                        this.cartItems.assets.length > 0
                    ) {
                        const cartItem = this.cartItems.assets.find(
                            (obj) => obj.editionIds[0]
                        );
                        this.cartStore
                            .pipe(select(fromCartStore.getProgramEditionId))
                            .pipe(first(), takeUntil(this.ngUnsubscribe))
                            .subscribe((editionIdFromStore: EditionId) => {
                                if (editionIdFromStore) {
                                    this.editionId = editionIdFromStore;
                                } else if (cartItem) {
                                    this.editionId = cartItem.editionIds[0];
                                }
                            });
                    }

                    this.onContentLibraryPage =
                        router.state.url.includes("assign") &&
                        !router.state.url.includes("preview") &&
                        !router.state.url.includes("assignment") &&
                        !router.state.url.includes("review");
                }
            });

        this.cartStore
            .pipe(select(fromCartStore.getContainsComposite))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((containsComposite: boolean) => {
                this.containsComposite = containsComposite;
                this.cdr.detectChanges();
            });

        this.cartStore
            .pipe(select(fromCartStore.getLessonFormData))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data: Bundle | null) => {
                if (data) {
                    if (data.assetId) {
                        this.lessonFormData = data;
                    } else {
                        // otherwise this is creatingLesson state and lessonID is assigned when form is complete
                        this.lessonFormData = data;
                    }
                }
                this.cdr.detectChanges();
            });
        this.cartStore
            .pipe(select(fromCartStore.getLessonFormValidity))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((validity: boolean) => {
                this.lessonFormValidity = validity;
                this.cdr.detectChanges();
            });

        this.cartStore
            .pipe(select(fromCartStore.getCustomActivityFormData))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data: CustomActivity | null) => {
                if (data) {
                    this.activityFormData = data;
                }
                this.cdr.detectChanges();
            });
        this.cartStore
            .pipe(select(fromCartStore.getActivityFormValidity))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((validity: boolean) => {
                this.activityFormValidity = validity;
                this.cdr.detectChanges();
            });

        this.createStore
            .pipe(select(fromCreateStore.getAssetsTab))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((selectedIndex: number) => {
                this.assetsPageTab = selectedIndex;
            });

        this.coursesStore
            .pipe(select(fromCoursesStore.getSelectedCourseObject))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((selectedCourse: Course | null) => {
                this.selectedCourse = selectedCourse;
                if (selectedCourse) {
                    this.subjectId = selectedCourse.subjectId;
                }
            });
        this.assetInUse$ = this.cartStore.pipe(
            select(fromCartStore.getAssetInUse)
        );
    }

    scrollToLastAddedAsset() {
        const assetElementArray: HTMLElement[] =
                this.assetScroller.nativeElement.getElementsByClassName(
                    "assign-cart-assignable"
                ),
            lastAssetElement = assetElementArray[assetElementArray.length - 1];
        lastAssetElement.scrollIntoView();
    }

    drop(event: CdkDragDrop<string[]>) {
        if (this.cartItems) {
            const clone = [...this.cartItems.assets];
            moveItemInArray(clone, event.previousIndex, event.currentIndex);
            this.cartItems = { ...this.cartItems, assets: clone };
            this.createStore.dispatch(
                fromCreateStore.SetSelectedAssets({ payload: this.cartItems })
            );
        }
    }

    getTotalPacing(selectedAssets: SelectedAssets | undefined) {
        return (
            selectedAssets &&
            this.pacingHelper.getTotalPacing(selectedAssets.assets)
        );
    }

    // Get Cart Items count for badge
    getCartItems(cartItems: SelectedAssets | undefined) {
        if (cartItems && cartItems.assets.length > 0) {
            return cartItems.assets.length;
        }
    }

    // Get selected assignable name
    getAssignableName(item: Asset) {
        if (item) {
            return this.assignmentHelper.getAssetDisplayName(item);
        }
    }

    // Get selected assignable type icon
    getIcon(item: Asset) {
        if (item) {
            if (item.assetActivityTypeId) {
                return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                    item.assetActivityTypeId
                );
            }
            return this.assetTypeHelper.convertShortAssignableTypeToIconPath(
                item.assetTypeId
            );
        } else {
            return "";
        }
    }

    getToolTip(asset: Asset) {
        if (asset) {
            let assetType: string = asset.assetTypeId;
            if (asset.assetActivityTypeId) {
                assetType = asset.assetActivityTypeId;
            }
            return this.assetTypeHelper.getCapTypeName(assetType);
        }
    }

    // Remove selected assignable
    removeSelectedAssignable(assignable: Asset) {
        if (this.cartItems) {
            // Clone the selected assignables array because typescript locked the object.
            const selectedAssets = this.cartItems.assets.slice();
            const foundAtIndex = this.getSelectedAssetIndex(assignable);

            if (foundAtIndex >= 0) {
                selectedAssets.splice(foundAtIndex, 1);

                this.cartItems = { ...this.cartItems, assets: selectedAssets };
                this.createStore.dispatch(
                    fromCreateStore.SetSelectedAssets({
                        payload: this.cartItems,
                    })
                );
            }
        }

        // If last assignable removed, close Assign cart
        if (
            this.cartItems &&
            this.cartItems.assets.length === 0 &&
            !this.creatingLesson &&
            !this.creatingActivity
        ) {
            this.cartStore.dispatch(
                fromCartStore.setAssignCartOpen({ payload: false })
            );
            this.sidePanelRightClose.emit();
        }

        if (this.editingLesson) {
            this.cartStore.dispatch(
                fromCartStore.setLessonFormValidity({ payload: false })
            );
        }
    }

    // display correct Convert to Lesson text
    getConvertToLessonText() {
        if (this.cartItems && this.cartItems.assets.length > 0) {
            return "Convert to Bundle";
        } else {
            return "Create Bundle";
        }
    }

    // display correct Assign text
    getAssignButtonText() {
        if (this.creatingLesson && !this.editingLesson) {
            // If Lesson Builder
            return "Create & Assign";
        } else if (this.creatingLesson && this.editingLesson) {
            // If Lesson Builder
            return "Update & Assign";
        } else {
            return "Assign";
        }
    }

    getCreateEditButtonText() {
        if (this.editingLesson || this.editingActivity) {
            return "Update";
        } else {
            return "Create";
        }
    }

    // Convert to Lesson
    convertToLessonBuilder() {
        this.cartStore.dispatch(
            fromCartStore.setCreatingLesson({ payload: true })
        );
    }

    // Open Create Activity section
    convertToActivityBuilder() {
        this.cartStore.dispatch(
            fromCartStore.setCreatingActivity({ payload: true })
        );
    }

    handleAddClick() {     
        this.addAssets();        
        // Land on Assignables tab after clicking on Add button or + button on side panel
        this.localStorage.set("contentLibrarySelectedIndex", 1);
        this.createStore.dispatch(fromCreateStore.SetAssetsTab({ payload: 1 }));
        
    }

    // Minimize cart and select more assets
    addAssets() {
        if (this.cartItems && this.cartItems.assets.length > 0) {
            this.cartStore.dispatch(
                fromCartStore.setAssignCartTabOpen({ payload: true })
            );
        } else {
            this.cartStore.dispatch(
                fromCartStore.setAssignCartTabOpen({ payload: false })
            );
        }
        this.cartStore.dispatch(
            fromCartStore.setAssignCartOpen({ payload: false })
        );

        if (this.editionId && !this.onContentLibraryPage) {
            this.router.navigate([`/teacher/assign/${this.editionId}`]);
        } else if (
            // If on Content Library landing page, and you have selected assets
            this.editionId &&
            this.editionId === "LANDING" &&
            this.onContentLibraryPage &&
            this.cartItems &&
            this.cartItems.editionId
        ) {
            this.router.navigate([
                `/teacher/assign/${this.cartItems.editionId}`,
            ]);
        } else if (this.activeEditions?.length === 1) {
            const lID = this.activeEditions[0].editionId;
            this.router.navigate([`/teacher/assign/${lID}`]);
        }
        this.sidePanelRightClose.emit();
    }

    goToAssignReview(editionId: string | null) {
        if (editionId) {
            this.editionId = editionId;
        }

        this.localStorage.remove("autoProgressAssignables");
        this.assignStore.dispatch(
            fromAssignStore.clearAutoProgressAssignables()
        );

        // Close Side Cart
        // this.cartStore.dispatch(fromCreateStore.clearClonedAssignable());
        this.cartStore.dispatch(
            fromCartStore.setAssignCartOpen({ payload: false })
        );
        this.sidePanelRightClose.emit();

        // Route to Assign - Review page
        if (this.editionId) {
            this.router.navigate([`/teacher/assign/${this.editionId}/review`]);
        } else if (this.activeEditions?.length === 1) {
            const lID = this.activeEditions[0].editionId;
            this.router.navigate([`/teacher/assign/${lID}/review`]);
        }
        this.preventDoubleClick = false;
    }

    // --------- Cart Closing
    // Close panel and show assign cart tab if still assigning
    closeSidePanel() {
        if (this.editingLesson) {
            this.cartStore.dispatch(
                fromCartStore.setAssignCartTabOpen({ payload: false })
            );
            this.cartItems = { ...this.cartItems, assets: [] };
            this.createStore.dispatch(
                fromCreateStore.SetSelectedAssets({
                    payload: this.cartItems,
                })
            );
            this.createStore.dispatch(fromCreateStore.clearLesson());
            this.createStore.dispatch(fromCreateStore.clearClonedAsset());
        } else if (this.cartItems && this.cartItems.assets.length > 0) {
            this.cartStore.dispatch(
                fromCartStore.setAssignCartTabOpen({ payload: true })
            );
        } else {
            this.cartStore.dispatch(
                fromCartStore.setAssignCartTabOpen({ payload: false })
            );
        }
        this.cartStore.dispatch(
            fromCartStore.setAssignCartOpen({ payload: false })
        );
        if (this.editingActivity || this.creatingActivity) {
            this.cartStore.dispatch(
                fromCartStore.setEditingActivity({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setCreatingActivity({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setCustomActivityFormData({ payload: null })
            );
            this.cartStore.dispatch(
                fromCartStore.setPerformActivityFormData({ payload: null })
            );
            this.cartStore.dispatch(
                fromCartStore.setActivityFormValidity({ payload: false })
            );
        } else if (this.editingLesson || this.creatingLesson) {
            this.cartStore.dispatch(
                fromCartStore.setEditingLesson({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setCreatingLesson({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setLessonFormData({ payload: null })
            );
            this.cartStore.dispatch(
                fromCartStore.setLessonFormValidity({ payload: false })
            );
        }
        this.sidePanelRightClose.emit();
    }

    getSelectedAssetIndex(asset: Asset) {
        let foundAtIndex = -1;
        let index = 0;

        this.cartItems.assets.forEach(function (a) {
            if (a.assetId === asset.assetId) {
                foundAtIndex = index;
                return;
            }
            index++;
        });
        return foundAtIndex;
    }

    // ---------- CREATE and EDIT
    create() {
        if (!this.preventDoubleClick) {
            if (this.editingLesson && this.lessonFormData) {
                this.onUpdateLesson(this.lessonFormData, false);
            } else if (
                this.creatingLesson &&
                !this.editingLesson &&
                this.lessonFormData
            ) {
                this.onCreateLesson(this.lessonFormData, false);
            }
        }
    }
    createAndAssign() {
        if (!this.preventDoubleClick) {
            if (this.editingLesson && this.lessonFormData) {
                this.onUpdateLesson(this.lessonFormData, true);
            } else if (
                this.creatingLesson &&
                !this.editingLesson &&
                this.lessonFormData
            ) {
                this.onCreateLesson(this.lessonFormData, true);
            } else {
                this.goToAssignReview(this.editionId ? this.editionId : null);
            }
        }
    }

    onCreateLesson(lessonForm: Bundle, createAdd: boolean) {
        this.preventDoubleClick = true;

        lessonForm = {
            ...lessonForm,
            editionIds: lessonForm.editionIds,
            bundledAssetIds: this.cartItems.assets.map((t) => t.assetId),
            assetActivityTypeId: lessonForm.assetActivityTypeId, // assetActivityTypeId is also "BUNDLE" just like assetTypeId
        };

        // If creating bundle and we're going to assign it right away, set these
        let createAddBundle = false;
        if (createAdd) {
            this.createAddBundle = true;
            createAddBundle = true;
        }
        this.createStore.dispatch(
            fromCreateStore.createAsset({
                payload: lessonForm,
                createAdd,
                createAddBundle,
            })
        );
        this.cartStore.dispatch(
            fromCartStore.setLessonFormData({ payload: null })
        );
        this.cartStore.dispatch(
            fromCartStore.setCreatingLesson({ payload: false })
        );
        this.cartStore.dispatch(
            fromCartStore.setLessonFormValidity({
                payload: false,
            })
        );
        this.preventDoubleClick = false;
        // Subscribe to the createAssetSuccess action to know when the bundle is successfully created
        this.actions$.pipe(
            ofType(assetsActions.createAssetSuccess),
            ).subscribe((action) => {
            if(action){
                if (createAdd) {
                    this.cartStore.dispatch(
                        fromCartStore.setContainsComposite({
                            payload: true,
                        })
                    );
                    // this.goToAssignReview(null);
                } else {
                    // Selected items needed to be removed from the cart after the bundle is created.
                    this.cartItems = { ...this.cartItems, assets: [] };
                    this.createStore.dispatch(
                        fromCreateStore.SetSelectedAssets({
                            payload: this.cartItems,
                        })
                    );
                    // Go back to Content Library
                    this.routeToAssignablesTab();
                    this.cartStore.dispatch(
                        fromCartStore.setAssignCartOpen({ payload: false })
                    );
                    this.localStorage.remove("selectedAssets");
                    this.cartItems = { ...this.cartItems, assets: [] };
                    this.createStore.dispatch(
                        fromCreateStore.SetSelectedAssets({
                            payload: this.cartItems,
                        })
                    );
                    this.sidePanelRightClose.emit();
                }
            }
        }); 
    }

    routeToAssignablesTab() {
        this.router.navigate([`teacher/assign/${this.editionId}`]);
    }

    onUpdateLesson(lessonForm: Bundle, assign: boolean) {
        this.preventDoubleClick = true;
        this.updatingBundle = true;
        this.updatedBundleAssetId = lessonForm.assetId;

        lessonForm = {
            ...lessonForm,
            editionIds: lessonForm.editionIds,
            bundledAssetIds: this.cartItems.assets.map((t) => t.assetId),
            assetActivityTypeId: assetActivityTypeIds.BUNDLE,
        };

        this.createStore.dispatch(
            fromCreateStore.updateAsset({ payload: lessonForm })
        );

        this.createStore
            .pipe(select(fromCreateStore.getAssetPaginationParams))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((params) => {
                this.params = params;
            });

        this.cartStore.dispatch(
            fromCartStore.setLessonFormData({ payload: null })
        );
        // clear lesson form data otherwise it is visible on next bundle creation
        this.cartStore.dispatch(fromCreateStore.clearLesson());

        if (assign) {
            this.cartStore.dispatch(
                fromCartStore.setContainsComposite({
                    payload: true,
                })
            );
            this.cartStore.dispatch(
                fromCartStore.setCreatingLesson({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setEditingLesson({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setLessonFormValidity({
                    payload: false,
                })
            );

            this.createStore
                .pipe(select(fromCreateStore.getUpdatedBundle))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((updatedBundle: Asset | null) => {
                    if (updatedBundle && this.updatingBundle) {
                        this.updatingBundle = false;
                        this.cartItems = { ...this.cartItems, assets: [] };
                        const sAssets = [
                            ...this.cartItems.assets,
                            updatedBundle,
                        ];
                        this.cartItems = { ...this.cartItems, assets: sAssets };
                        this.createStore.dispatch(
                            fromCreateStore.SetSelectedAssets({
                                payload: this.cartItems,
                            })
                        );
                        this.editingLesson = false;
                        this.createStore.dispatch(
                            fromCreateStore.clearClonedAsset()
                        );
                        this.createStore.dispatch(
                            fromCreateStore.clearUpdatedBundle()
                        );
                        this.preventDoubleClick = false;
                        this.goToAssignReview(null);
                    }
                });
        } else {
            this.cartStore.dispatch(
                fromCartStore.setAssignCartOpen({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setCreatingLesson({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setEditingLesson({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setLessonFormValidity({
                    payload: false,
                })
            );
            this.localStorage.remove("selectedAssets");
            this.cartItems = { ...this.cartItems, assets: [] };
            this.createStore.dispatch(
                fromCreateStore.SetSelectedAssets({
                    payload: this.cartItems,
                })
            );
            this.createStore.dispatch(fromCreateStore.clearClonedAsset());
            this.sidePanelRightClose.emit();
            this.preventDoubleClick = false;
            this.routeToAssignablesTab();
        }
    }

    getCreateDisabled() {
        if (
            this.creatingLesson &&
            (this.cartItems.assets.length === 0 || !this.lessonFormValidity)
        ) {
            return true;
        } else if (this.creatingActivity) {
            return true;
        } else if (!this.creatingLesson && this.cartItems.assets.length === 0) {
            return true;
        }
    }

    clearCartWarning() {
        if (!this.creatingLesson && !this.editingLesson) {
            const dialogRef = this.dialog.open(
                ClearSelectedAssignablesDialogComponent,
                { data: { containsBundle: false } }
            );

            dialogRef.afterClosed().subscribe((result) => {
                if (result === "regular_clear") {
                    this.cartItems = { ...this.cartItems, assets: [] };
                    this.createStore.dispatch(
                        fromCreateStore.SetSelectedAssets({
                            payload: this.cartItems,
                        })
                    );
                    this.cartStore.dispatch(
                        fromCartStore.setAssignCartOpen({ payload: false })
                    );
                    this.cartStore.dispatch(
                        fromCartStore.setCreatingLesson({ payload: false })
                    );
                    this.cartStore.dispatch(
                        fromCartStore.setEditingLesson({ payload: false })
                    );
                    this.cartStore.dispatch(
                        fromCartStore.setLessonFormValidity({ payload: false })
                    );
                    this.localStorage.remove("selectedAssets");
                    this.preventDoubleClick = false;
                    this.sidePanelRightClose.emit();
                    this.cartStore.dispatch(
                        fromCartStore.setLessonFormData({ payload: null })
                    );
                    this.cartStore.dispatch(fromCreateStore.clearLesson());
                    this.routeToCurrentPage();
                }
            });
        } else if (this.creatingLesson || this.editingLesson) {
            const dialogRef = this.dialog.open(
                ClearSelectedAssignablesDialogComponent,
                { data: { containsBundle: true } }
            );

            dialogRef.afterClosed().subscribe((result) => {
                if (result === "bundle_clear") {
                    this.cartItems = { ...this.cartItems, assets: [] };
                    this.createStore.dispatch(
                        fromCreateStore.SetSelectedAssets({
                            payload: this.cartItems,
                        })
                    );
                    this.localStorage.remove("selectedAssets");
                }
            });
        }
    }

    ifMathProgram() {
        if (this.subjectId === "MATH") {
            return true;
        }
    }

    routeToCurrentPage() {
        if (this.onContentLibraryPage && this.assetsPageTab) {
            this.createStore.dispatch(
                fromCreateStore.SetAssetsTab({
                    payload: this.assetsPageTab,
                })
            );
        }
        this.router.navigate([this.currentPageURL]);
    }

    // Show No Activities Selected for Bundles
    showNoActivitiesSelectedForBundles() {
        if (
            ((!this.cartItems ||
                (this.cartItems && this.cartItems.assets.length === 0)) &&
                this.creatingLesson) ||
            (this.editingLesson && !this.cartItems)
        ) {
            return true;
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}

@Component({
    selector: "clear-selected-assignables-dialog",
    templateUrl: "clear-selected-assignables-dialog.html",
    standalone: false
})
export class ClearSelectedAssignablesDialogComponent {
    constructor(
        public dialogRef: MatDialogRef<ClearSelectedAssignablesDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {}

    cancel(): void {
        this.dialogRef.close();
    }

    clear() {
        if (this.data.containsBundle) {
            this.dialogRef.close("bundle_clear");
        } else if (!this.data.containsBundle) {
            this.dialogRef.close("regular_clear");
        }
    }
}
